import * as React from 'react';
import { TileType, getTileColor, getTileName } from '../../../../shared/TileEditorTypes';

interface PropertiesPanelProps {
  selectedTile?: {
    type: TileType;
    x: number;
    y: number;
    layer: string;
  };
  selectedEntity?: {
    id: string;
    type: string;
    x: number;
    y: number;
    properties: Record<string, any>;
  };
  onPropertyChange: (property: string, value: any) => void;
}

// Property keys that have dedicated UI controls elsewhere in this panel —
// excluded from the generic Custom Properties list so they aren't edited twice.
const RESERVED_PROPERTY_KEYS = new Set(['onEnter', 'onEnterTarget', 'onExit']);

// The entity kinds the editor can actually place (mirrors EntityPalette's
// EntityCategory). The dropdown previously omitted `mob` and `object`, so a
// placed mob had no matching option and couldn't be edited post-placement.
const ENTITY_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'npc', label: 'NPC' },
  { value: 'mob', label: 'Mob' },
  { value: 'spawn', label: 'Spawn Point' },
  { value: 'object', label: 'Object' },
  { value: 'teleporter', label: 'Teleporter' },
  { value: 'trigger', label: 'Trigger' },
];

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedTile,
  selectedEntity,
  onPropertyChange
}) => {
  const [customProperties, setCustomProperties] = React.useState<Record<string, string>>({});

  // Sync the custom-property list FROM the selected entity whenever the
  // selection changes, so existing properties are visible/editable instead of
  // always showing "No custom properties added". Reserved keys (which have
  // their own controls) are filtered out to avoid a duplicate editing path.
  React.useEffect(() => {
    if (!selectedEntity) {
      setCustomProperties({});
      return;
    }
    const synced: Record<string, string> = {};
    for (const [key, value] of Object.entries(selectedEntity.properties || {})) {
      if (RESERVED_PROPERTY_KEYS.has(key) || value === undefined || value === null) continue;
      synced[key] = String(value);
    }
    setCustomProperties(synced);
  }, [selectedEntity?.id]);

  const handleAddProperty = () => {
    const key = prompt('Enter property key:');
    const value = prompt('Enter property value:');
    if (key && value) {
      setCustomProperties(prev => ({ ...prev, [key]: value }));
      onPropertyChange(key, value);
    }
  };

  const handleRemoveProperty = (key: string) => {
    setCustomProperties(prev => {
      const newProps = { ...prev };
      delete newProps[key];
      return newProps;
    });
    onPropertyChange(key, undefined);
  };

  if (!selectedTile && !selectedEntity) {
    return (
      <div style={{
        width: '300px',
        backgroundColor: '#34495e',
        color: 'white',
        padding: '20px',
        borderLeft: '1px solid #2c3e50',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '15px'
      }}>
        <div style={{ fontSize: '48px', opacity: 0.5 }}>📋</div>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Properties</h3>
          <p style={{ opacity: 0.7, fontSize: '14px' }}>
            Select a tile or entity to edit its properties.
          </p>
        </div>
      </div>
    );
  }

  const target = selectedTile || selectedEntity;
  const isTile = !!selectedTile;
  const isEntity = !!selectedEntity;

  return (
    <div style={{
      width: '300px',
      backgroundColor: '#34495e',
      color: 'white',
      padding: '20px',
      borderLeft: '1px solid #2c3e50',
      height: '100%',
      overflowY: 'auto'
    }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        ⚙️ Properties
      </h3>

      {/* Target Preview */}
      <div style={{
        backgroundColor: '#2c3e50',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          backgroundColor: isTile ? getTileColor(selectedTile!.type) : '#3498db',
          borderRadius: '6px',
          border: '2px solid rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px'
        }}>
          {isTile ? '■' : '👤'}
        </div>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {isTile ? getTileName(selectedTile!.type) : selectedEntity!.type}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            {isTile ? 'Tile' : 'Entity'} • Layer: {isTile ? selectedTile!.layer : 'entities'}
          </div>
        </div>
      </div>

      {/* Basic Properties */}
      <div style={{ marginBottom: '25px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📍 Position
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
              X Position
            </label>
            <input
              type="number"
              value={target?.x || 0}
              disabled={isTile}
              title={isTile ? 'Tiles are grid-fixed — use Move/Eraser to relocate' : undefined}
              onChange={(e) => onPropertyChange('x', parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#2c3e50',
                color: 'white',
                border: '1px solid #4a6278',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isTile ? 0.6 : 1
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
              Y Position
            </label>
            <input
              type="number"
              value={target?.y || 0}
              disabled={isTile}
              title={isTile ? 'Tiles are grid-fixed — use Move/Eraser to relocate' : undefined}
              onChange={(e) => onPropertyChange('y', parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#2c3e50',
                color: 'white',
                border: '1px solid #4a6278',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isTile ? 0.6 : 1
              }}
            />
          </div>
        </div>
      </div>

      {/* Entity-Specific Properties */}
      {isEntity && (
        <div style={{ marginBottom: '25px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🎯 Entity Settings
          </h4>
          <div>
            <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
              Entity Type
            </label>
            <select
              value={selectedEntity.type}
              onChange={(e) => onPropertyChange('type', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#2c3e50',
                color: 'white',
                border: '1px solid #4a6278',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {ENTITY_TYPE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
              {/* Surface the entity's actual stored type when it isn't one of
                  the generic kinds (placed entities carry their palette id,
                  e.g. a mob's id) so the select reflects reality instead of
                  silently rendering blank. */}
              {!ENTITY_TYPE_OPTIONS.some(o => o.value === selectedEntity.type) && (
                <option value={selectedEntity.type}>{selectedEntity.type}</option>
              )}
            </select>
          </div>
        </div>
      )}

      {/* Trigger Settings (Specialized) */}
      {isEntity && selectedEntity.type === 'trigger' && (
        <div style={{ marginBottom: '25px', backgroundColor: 'rgba(255, 100, 100, 0.1)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 100, 100, 0.3)' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚡ Trigger Logic
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
                On Enter Action
              </label>
              <select
                value={selectedEntity.properties?.onEnter || ''}
                onChange={(e) => onPropertyChange('onEnter', e.target.value)}
                style={{ width: '100%', padding: '6px', backgroundColor: '#2c3e50', color: 'white', border: '1px solid #4a6278', borderRadius: '4px' }}
              >
                <option value="">None</option>
                <option value="teleport">Teleport Player</option>
                <option value="spawnMob">Spawn Mob</option>
                <option value="playDialogue">Play Dialogue</option>
                <option value="damage">Deal Damage</option>
                <option value="custom">Custom Event</option>
              </select>
            </div>

            {selectedEntity.properties?.onEnter && (
              <div>
                <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
                  Target / Payload
                </label>
                <input
                  type="text"
                  placeholder="e.g. map_id, mob_id, dialogue_id"
                  value={selectedEntity.properties?.onEnterTarget || ''}
                  onChange={(e) => onPropertyChange('onEnterTarget', e.target.value)}
                  style={{ width: '100%', padding: '6px', backgroundColor: '#2c3e50', color: 'white', border: '1px solid #4a6278', borderRadius: '4px' }}
                />
              </div>
            )}
            
            <div>
              <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
                On Exit Action
              </label>
              <select
                value={selectedEntity.properties?.onExit || ''}
                onChange={(e) => onPropertyChange('onExit', e.target.value)}
                style={{ width: '100%', padding: '6px', backgroundColor: '#2c3e50', color: 'white', border: '1px solid #4a6278', borderRadius: '4px' }}
              >
                <option value="">None</option>
                <option value="despawnMob">Despawn Mob</option>
                <option value="custom">Custom Event</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tile-Specific Properties */}
      {isTile && (
        <div style={{ marginBottom: '25px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🧱 Tile Settings
          </h4>
          <div>
            <label style={{ fontSize: '12px', opacity: 0.8, display: 'block', marginBottom: '5px' }}>
              Tile Type
            </label>
            <div style={{
              padding: '10px',
              backgroundColor: '#2c3e50',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: getTileColor(selectedTile.type),
                borderRadius: '3px'
              }} />
              <div>{getTileName(selectedTile.type)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Properties */}
      <div style={{ marginBottom: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h4 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🔧 Custom Properties
          </h4>
          <button
            onClick={handleAddProperty}
            style={{
              padding: '6px 12px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            ➕ Add
          </button>
        </div>

        {Object.keys(customProperties).length === 0 ? (
          <div style={{
            padding: '15px',
            backgroundColor: '#2c3e50',
            borderRadius: '6px',
            textAlign: 'center',
            opacity: 0.7,
            fontSize: '14px'
          }}>
            No custom properties added.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(customProperties).map(([key, value]) => (
              <div key={key} style={{
                backgroundColor: '#2c3e50',
                padding: '10px',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{key}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>{value}</div>
                </div>
                <button
                  onClick={() => handleRemoveProperty(key)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => {
            if (isTile) {
              onPropertyChange('type', TileType.EMPTY);
            } else {
              onPropertyChange('remove', true);
            }
          }}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          🗑️ {isTile ? 'Clear Tile' : 'Remove Entity'}
        </button>
        <button
          onClick={() => {
            const json = JSON.stringify(target, null, 2);
            navigator.clipboard.writeText(json);
            alert('Copied to clipboard!');
          }}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📋 Copy JSON
        </button>
      </div>
    </div>
  );
};
